import type { ComponentType } from 'react';
import {
  CalendarIcon,
  ChatIcon,
  CreditCardIcon,
  LockIcon,
  MapPinIcon,
  ShieldCheckIcon,
  StarIcon,
  UserCheckIcon,
  UsersIcon,
  type IconProps,
} from '@/components/ui/Icons';

/**
 * Static copy for the marketing surface, matching the approved landing design.
 *
 * Nothing here is fetched — the jobs module (M05) owns real listings, and the
 * pricing block is explicitly a placeholder until Q2 is answered (DECISIONS.md).
 * Keeping the copy in one file means swapping a section over to live data later
 * is a change of source, not a rewrite of the markup.
 */

type Icon = ComponentType<IconProps>;

/* -------------------------------------------------------------------------- */
/* Hero                                                                       */
/* -------------------------------------------------------------------------- */

export const HERO_TRUST: { label: string; icon: Icon }[] = [
  { label: 'SSL Secured', icon: LockIcon },
  { label: 'Parent Approval Required', icon: UsersIcon },
  { label: 'Adults ID - Verified (18+)', icon: ShieldCheckIcon },
];

/** The dark strip directly under the hero. */
export const PLATFORM_FEATURES: { title: string; caption: string; icon: Icon }[] = [
  { title: 'Identity Verification', caption: 'Verified Users', icon: UserCheckIcon },
  { title: 'Safe Messaging', caption: 'In-App', icon: ChatIcon },
  { title: 'Smart Matching', caption: 'For Skills & Location', icon: MapPinIcon },
  { title: 'Work Management', caption: 'Track & Communicate', icon: CalendarIcon },
  { title: 'Secure Payments', caption: 'Fast & Protected', icon: CreditCardIcon },
  { title: 'Ratings & Reviews', caption: 'Build Reputation', icon: StarIcon },
];

/* -------------------------------------------------------------------------- */
/* "Stop asking for money"                                                    */
/* -------------------------------------------------------------------------- */

/** The floating labels beside the photo. `tint` styles the pill. */
export const SKILL_PILLS: { label: string; emoji: string; tint: string }[] = [
  { label: 'Dog Walking', emoji: '🐾', tint: 'bg-safe-50 text-safe-700' },
  { label: 'Tutoring', emoji: '📖', tint: 'bg-sky-50 text-sky-700' },
  { label: 'Sell Your Art', emoji: '🎨', tint: 'bg-rose-50 text-rose-700' },
  { label: '& More!', emoji: '💻', tint: 'bg-accent-100 text-accent-700' },
];

export const EARNING_IDEAS: {
  name: string;
  emoji: string;
  description: string;
  rate: string;
  chip: string;
  pill: string;
}[] = [
  {
    name: 'Dog Walker',
    emoji: '🐶',
    description: 'Take dogs for a walk in your area.',
    rate: '$15/hr',
    chip: 'bg-sun-100',
    pill: 'bg-sun-100 text-sun-700',
  },
  {
    name: 'Tutoring',
    emoji: '🎓',
    description: 'Help others learn and grow.',
    rate: '$20/hr',
    chip: 'bg-indigo-100',
    pill: 'bg-indigo-100 text-indigo-700',
  },
  {
    name: 'Sell Art',
    emoji: '🎨',
    description: 'Turn your creativity into cash.',
    rate: 'Set your price',
    chip: 'bg-rose-100',
    pill: 'bg-rose-100 text-rose-700',
  },
];

/* -------------------------------------------------------------------------- */
/* Choose your path                                                           */
/* -------------------------------------------------------------------------- */

export interface PathContent {
  key: 'teen' | 'teenPlus';
  name: string;
  ages: string;
  bullets: string[];
  cta: string;
}

export const PATHS: PathContent[] = [
  {
    key: 'teen',
    name: 'Teen',
    ages: '(Ages 14–17)',
    bullets: [
      'Safe local gigs & tasks',
      'Skill-building opportunities',
      'Parent-supported every step',
    ],
    cta: 'Explore as a Teen',
  },
  {
    key: 'teenPlus',
    name: 'Teen+',
    ages: '(Ages 18–21)',
    bullets: [
      'More independent opportunities',
      'Internships & flexible work',
      'Build experience & early career',
    ],
    cta: 'Explore as Teen+',
  },
];

/* -------------------------------------------------------------------------- */
/* How it works                                                               */
/* -------------------------------------------------------------------------- */

export interface StepContent {
  number: string;
  title: string;
  description: string;
}

export const HOW_IT_WORKS: StepContent[] = [
  {
    number: '1',
    title: 'Create Your Profile',
    description: 'Showcase your skills, interests and what you are good at.',
  },
  {
    number: '2',
    title: 'Get Parent Approval',
    description:
      'A parent or guardian reviews and approves your profile before it goes live, and can check in on your activity anytime.',
  },
  {
    number: '3',
    title: 'Find Or Create',
    description: 'Discover opportunities or offer your own services.',
  },
  {
    number: '4',
    title: 'Connect & Get Approved',
    description: 'Communicate, agree on details, and get approved.',
  },
  {
    number: '5',
    title: 'Do Great Work',
    description: 'Deliver quality, build your reputation, earn & grow.',
  },
  {
    number: '6',
    title: 'Get Paid & Grow',
    description: 'Receive secure payments, collect reviews, level up.',
  },
];

/* -------------------------------------------------------------------------- */
/* Pricing — placeholder until Q2 is answered (DECISIONS.md)                  */
/* -------------------------------------------------------------------------- */

export interface PricingTier {
  audience: string;
  headline: string;
  points: string[];
  /** The middle card is the highlighted one. */
  featured?: boolean;
  flag?: string;
}

export const PRICING_TIERS: PricingTier[] = [
  {
    audience: 'Teens & Teen+',
    headline: 'Free to join',
    points: [
      'Create a profile at no cost',
      'Browse and apply to opportunities',
      'Small service fee only when you get paid',
    ],
  },
  {
    audience: 'Young Entrepreneurs',
    headline: 'Free to start your business',
    flag: 'MOST FLEXIBLE',
    featured: true,
    points: [
      'List unlimited services',
      'Manage clients & earnings in one place',
      'Fee applies per completed job',
    ],
  },
  {
    audience: 'Job Posters',
    headline: 'Free to post a job',
    points: [
      'Post as many opportunities as you like',
      'Access verified, background-checked talent',
      'Optional featured-listing upgrade',
    ],
  },
];

/* -------------------------------------------------------------------------- */
/* Built on trust                                                             */
/* -------------------------------------------------------------------------- */

export interface TrustPillar {
  title: string;
  description: string;
  icon: Icon;
  /** Card surface and border — one tint per pillar. */
  surface: string;
  chip: string;
}

export const TRUST_PILLARS: TrustPillar[] = [
  {
    title: 'The TeenTack Team',
    description:
      'TeenTack was built with parents, safety advisors, and young people at the table from the start - not bolted on after launch. Every safeguard you see is live from day one.',
    icon: UsersIcon,
    surface: 'border-brand-200 bg-gradient-to-b from-brand-50 to-white',
    chip: 'bg-brand-600 text-white',
  },
  {
    title: 'Founding Members',
    description:
      'We are opening founding member spots to the first families and young entrepreneurs who join. Early members help shape how the platform grows.',
    icon: StarIcon,
    surface: 'border-sun-200 bg-gradient-to-b from-sun-50 to-white',
    chip: 'bg-sun-400 text-ink',
  },
  {
    title: 'Our Safety Commitment',
    description:
      'No opportunity goes live until the poster is verified, and no teen accepts a job until a parent has reviewed and approved it. That process does not change as we grow.',
    icon: ShieldCheckIcon,
    surface: 'border-safe-200 bg-gradient-to-b from-safe-50 to-white',
    chip: 'bg-safe-600 text-white',
  },
];

/* -------------------------------------------------------------------------- */
/* Audience sections                                                          */
/* -------------------------------------------------------------------------- */

export const PARENT_POINTS: string[] = [
  'Approve or decline every opportunity before it is accepted',
  'See verified poster identity and job details upfront',
  'Get notified the moment your teen is contacted',
];

export const POSTER_POINTS: string[] = [
  'Every poster completes identity verification',
  'Post jobs in minutes, manage applicants in-app',
  'Pay securely through the platform, not cash',
];

/* -------------------------------------------------------------------------- */
/* FAQ                                                                        */
/* -------------------------------------------------------------------------- */

export interface FaqGroup {
  heading: string;
  items: { question: string; answer: string }[];
}

export const FAQ_GROUPS: FaqGroup[] = [
  {
    heading: 'For Parents',
    items: [
      {
        question: 'How do you verify who my teen is working with?',
        answer:
          'Every job poster completes identity verification before a single listing goes live, and their verified status is shown on the job and on their profile.',
      },
      {
        question: 'Is my child’s personal information kept private?',
        answer:
          'Contact details are never shown on a profile or a listing. Messaging stays inside TeenTack until you approve a job, and you can see every conversation your teen has.',
      },
      {
        question: 'How are payments handled?',
        answer:
          'Agreed rates are recorded on the job and paid through the platform, so the amount and the date are never in dispute and no cash changes hands.',
      },
      {
        question: 'Is TeenTack compliant with child privacy laws?',
        answer:
          'Accounts for under-18s require verified guardian consent before they go live, and we only collect what a job needs. The launch jurisdiction sets the exact ruleset, confirmed before public launch.',
      },
    ],
  },
  {
    heading: 'For Teens & Young Adults',
    items: [
      {
        question: 'How old do I need to be to join?',
        answer:
          'From 14. Ages 14–17 join as a Teen with a parent or guardian approving the profile; 18–21 join as Teen+ and manage their own account.',
      },
      {
        question: 'How do I get paid?',
        answer:
          'Payment is released through the platform once the job is marked complete. Every payment lands in your earnings history, so you always know what you made and when.',
      },
      {
        question: 'Can I offer my own services, not just apply to jobs?',
        answer:
          'Yes. List what you do — dog walking, tutoring, art, anything you are good at — and let people nearby book you.',
      },
      {
        question: 'What if something goes wrong with a job?',
        answer:
          'Report it from the job itself. Support picks it up, your guardian is notified where one is attached, and payment is held until it is resolved.',
      },
    ],
  },
];

/* -------------------------------------------------------------------------- */
/* Waitlist                                                                   */
/* -------------------------------------------------------------------------- */

export const WAITLIST_ROLES: string[] = [
  'Teen (14–17)',
  'Teen+ (18–21)',
  'Young Entrepreneur',
  'Parent / Guardian',
  'Job Poster',
];

/* -------------------------------------------------------------------------- */
/* Footer                                                                     */
/* -------------------------------------------------------------------------- */

export interface FooterColumn {
  heading: string;
  links: { label: string; to: string }[];
}

export const FOOTER_COLUMNS: FooterColumn[] = [
  {
    heading: 'Platform',
    links: [
      { label: 'How It Works', to: '/#how-it-works' },
      { label: 'For Teens', to: '/#categories' },
      { label: 'For Teen+', to: '/#categories' },
      { label: 'For Young Entrepreneurs', to: '/#pricing' },
      { label: 'For Parents', to: '/#for-parents' },
      { label: 'For Job Posters', to: '/#for-posters' },
    ],
  },
  {
    heading: 'Company',
    links: [
      { label: 'About Us', to: '/#safety' },
      { label: 'Contact Us', to: '/support' },
    ],
  },
  {
    heading: 'Support',
    links: [
      { label: 'Help Center', to: '/support' },
      { label: 'Safety Tips', to: '/safety' },
    ],
  },
];

export const SUPPORT_EMAIL = 'support@teentack.com';
export const SUPPORT_PHONE = '1-800-XXX-XXXX';
